// src/components/SupabaseProtectedRoute.js - 既存機能保持版
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { supabase } from '../supabase';
import { useOrganization } from '../contexts/OrganizationContext';

function SupabaseProtectedRoute({ children }) {
  const location = useLocation();
  const [authState, setAuthState] = useState({
    loading: true,
    authenticated: false,
    user: null
  });

  // OrganizationContextの状態を取得
  const { isLoading: orgLoading } = useOrganization();

  console.log('🔐 SupabaseProtectedRoute: 認証チェック開始', location.pathname);

  useEffect(() => {
    // Supabase認証状態の確認
    const checkAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('❌ セッション取得エラー:', error);
          // エラー時はlocalStorageもクリア
          localStorage.removeItem('supabase_token');
          localStorage.removeItem('supabase_user');
          setAuthState({
            loading: false,
            authenticated: false,
            user: null
          });
          return;
        }
        
        if (session) {
          // ✅ セッションの有効期限チェック
          const now = Math.floor(Date.now() / 1000);
          if (session.expires_at && session.expires_at < now) {
            console.log('⏰ セッション期限切れ');
            await supabase.auth.signOut();
            localStorage.removeItem('supabase_token');
            localStorage.removeItem('supabase_user');
            setAuthState({
              loading: false,
              authenticated: false,
              user: null
            });
            return;
          }

          console.log('✅ 認証済みユーザー:', session.user.email);
          setAuthState({
            loading: false,
            authenticated: true,
            user: session.user
          });
        } else {
          // ✅ セッションがない場合、localStorageも確認するが、Supabaseが優先
          console.log('❌ Supabaseセッションなし');
          
          // localStorageもクリア（セッションがないのにlocalStorageがあるのは不整合）
          const token = localStorage.getItem('supabase_token');
          if (token) {
            console.log('⚠️ localStorageに残骸あり - クリア');
            localStorage.removeItem('supabase_token');
            localStorage.removeItem('supabase_user');
          }
          
          setAuthState({
            loading: false,
            authenticated: false,
            user: null
          });
        }
      } catch (error) {
        console.error('💥 認証チェックエラー:', error);
        // エラー時はlocalStorageもクリア
        localStorage.removeItem('supabase_token');
        localStorage.removeItem('supabase_user');
        setAuthState({
          loading: false,
          authenticated: false,
          user: null
        });
      }
    };

    checkAuth();

    // 認証状態の変更を監視
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('🔄 Auth状態変化:', event, location.pathname);
      
      if (event === 'SIGNED_OUT') {
        console.log('🚪 サインアウト検出 - localStorageクリア');
        // 🛡 セキュリティ: Internal モード関連・組織情報も明示的に消す。
        //    次回別アカウントログイン時の UI 誤表示 (別組織に成り代わって見える) を防ぐ。
        localStorage.removeItem('supabase_token');
        localStorage.removeItem('supabase_refresh_token');
        localStorage.removeItem('supabase_user');
        localStorage.removeItem('require_password_change');
        localStorage.removeItem('organizationId');
        localStorage.removeItem('employeeId');
        localStorage.removeItem('currentOrganization');
        localStorage.removeItem('internal_selected_org');
        localStorage.removeItem('internal_edit_session');
        sessionStorage.clear();
        setAuthState({
          loading: false,
          authenticated: false,
          user: null
        });
      } else if (event === 'SIGNED_IN' && session) {
        console.log('👤 サインイン検出');
        setAuthState({
          loading: false,
          authenticated: true,
          user: session.user
        });
      } else if (event === 'TOKEN_REFRESHED' && session) {
        console.log('🔄 トークン更新');
        setAuthState({
          loading: false,
          authenticated: true,
          user: session.user
        });
      } else if (session) {
        setAuthState({
          loading: false,
          authenticated: true,
          user: session.user
        });
      } else {
        console.log('❌ セッションなし');
        setAuthState({
          loading: false,
          authenticated: false,
          user: null
        });
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [location.pathname]);

  // ローディング中（認証チェック中 または OrganizationContext初期化中）
  if (authState.loading || orgLoading) {
    console.log('⏳ ローディング中...', { 
      authLoading: authState.loading, 
      orgLoading 
    });
    
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #1a365d 0%, #2563eb 100%)',
      }}>
        <div style={{
          textAlign: 'center',
          color: 'white',
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid rgba(255,255,255,0.3)',
            borderTop: '4px solid white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px',
          }}></div>
          <p style={{ fontSize: '1.1rem', fontWeight: '600' }}>読み込み中...</p>
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
      </div>
    );
  }

  // 認証されていない場合、ログインページにリダイレクト
  if (!authState.authenticated) {
    console.log('❌ 未認証 - ログインページへリダイレクト');
    return <Navigate to="/" replace />;
  }

  // パスワード変更が必要な場合
  const requirePasswordChange = localStorage.getItem('require_password_change');
  if (requirePasswordChange === 'true' && location.pathname !== '/change-password') {
    console.log('🔐 パスワード変更が必要 - リダイレクト');
    return <Navigate to="/change-password" replace />;
  }

  // 認証済みかつOrganizationContext初期化完了
  console.log('✅ 認証&初期化完了 - ページ表示');
  return children;
}

export default SupabaseProtectedRoute;